import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotice } from '../DialogProvider';
import styled from '@emotion/styled';
import { Settings, LogOut, RefreshCw, Home, Lock, FlaskConical, Check } from 'lucide-react';
import SocketService from '../../../../../packages/shared/socket';
import { useAuth } from '../AuthGate';
import { resolveEntryUrl } from '../../utils/participantEntry';

const MenuWrapper = styled.div`
  position: relative;
`;

const SettingsButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #555;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.05);
  }
`;

const SettingsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  min-width: 220px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 200;
  animation: slideDown 0.15s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownSection = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownLabel = styled.div`
  padding: 6px 16px;
  font-size: 11px;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: ${props => props.$danger ? '#ff4444' : props.$warning ? '#ffaa00' : '#fff'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: ${props => props.$danger ? 'rgba(255, 68, 68, 0.1)' : props.$warning ? 'rgba(255, 170, 0, 0.1)' : 'rgba(0, 153, 255, 0.1)'};
  }

  svg {
    opacity: 0.6;
  }
`;

/**
 * AdminSettingsMenu - the cog button and its drop-down: account and device items only.
 * Choosing an instance lives in InstanceSwitcher. Uses AuthContext for lock/logout.
 */
const AdminSettingsMenu = ({ instanceId, onGoHome, onRefreshConnection }) => {
  const navigate = useNavigate();
  const notice = useNotice();
  const { lock, logout, user } = useAuth();
  // Sitra staff = Entra-signed-in with a @sitra.fi address. Drives whether the
  // settings dropdown shows the device-sign-out item (clears the /screen PIN
  // device state) vs the Entra sign-out path (which AuthGate.logout owns).
  const isSitraSignedIn = !!user?.email?.toLowerCase().endsWith('@sitra.fi');

  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGoHome = () => {
    setOpen(false);
    onGoHome();
  };

  const handleRefreshConnection = () => {
    onRefreshConnection();
    setOpen(false);
  };

  const handleLogout = () => {
    setOpen(false);
    SocketService.disconnect();
    logout();
    // AuthGate will show login screen
  };

  // Device sign-out for PIN-authed (non-Sitra) users. AuthGate.logout() only
  // clears its in-memory state; the /screen flow writes its own localStorage
  // keys (`kuutio-instance`, `kuutio-screen-admin`, `kuutio-admin-auth`,
  // `kuutio-device-locked`, `kuutio-admin-instance`) which the auto-resume in
  // ScreenClient picks up next time. Without clearing those, "logging out"
  // just bounces straight back into the saved instance.
  const handleDeviceSignOut = () => {
    setOpen(false);
    SocketService.disconnect();
    ['kuutio-instance', 'kuutio-participant-instance', 'kuutio-screen-admin',
     'kuutio-admin-auth', 'kuutio-device-locked', 'kuutio-admin-instance']
      .forEach(k => localStorage.removeItem(k));
    logout();
    navigate('/screen', { replace: true });
  };

  // QA test panel on this device (localStorage, read by the QA panel through
  // the storage event). Device-local, so it lives here, not in instance settings.
  const [qaOn, setQaOn] = useState(() => {
    try { return localStorage.getItem('kuutio-qa-enabled') === 'true'; } catch { return false; }
  });
  const toggleQa = () => {
    const next = !qaOn;
    try { localStorage.setItem('kuutio-qa-enabled', String(next)); } catch { return; }
    window.dispatchEvent(new Event('storage'));
    setQaOn(next);
  };

  const handleLockDevice = async () => {
    setOpen(false);
    lock();
    // Navigate to participant view
    if (instanceId) {
      try {
        navigate(await resolveEntryUrl(instanceId));
      } catch (err) {
        console.error('Participant entry failed:', err);
        notice.error(err.message);
      }
    } else {
      navigate('/impactpath');
    }
  };

  return (
    <MenuWrapper ref={menuRef}>
      <SettingsButton onClick={() => setOpen(!open)} title="Asetukset">
        <Settings size={18} />
      </SettingsButton>

      {open && (
        <SettingsDropdown>
          <DropdownSection>
            <DropdownItem onClick={handleGoHome}>
              <Home size={16} />
              Admin-etusivu
            </DropdownItem>
          </DropdownSection>

          <DropdownSection>
            <DropdownLabel>Yhteys</DropdownLabel>
            <DropdownItem onClick={handleRefreshConnection}>
              <RefreshCw size={16} />
              Päivitä yhteys
            </DropdownItem>
          </DropdownSection>

          <DropdownSection>
            <DropdownLabel>Tämä laite</DropdownLabel>
            <DropdownItem onClick={toggleQa} aria-pressed={qaOn}>
              <FlaskConical size={16} />
              QA-testauspaneeli
              {qaOn && <Check size={14} style={{ marginLeft: 'auto' }} />}
            </DropdownItem>
            <DropdownItem $warning onClick={handleLockDevice}>
              <Lock size={16} />
              Lukitse laite
            </DropdownItem>
            {isSitraSignedIn ? (
              <DropdownItem $danger onClick={handleLogout}>
                <LogOut size={16} />
                Kirjaudu ulos
              </DropdownItem>
            ) : (
              <DropdownItem $danger onClick={handleDeviceSignOut}>
                <LogOut size={16} />
                Kirjaudu ulos / vaihda käyttäjää
              </DropdownItem>
            )}
          </DropdownSection>
        </SettingsDropdown>
      )}
    </MenuWrapper>
  );
};

export default AdminSettingsMenu;
